import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import SafeIcon from '../common/SafeIcon';

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function mergeRefs(...refs) {
  return (node) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    });
  };
}

const BrandVideoPlayer = forwardRef(function BrandVideoPlayer(
  {
    src,
    poster,
    className = '',
    videoClassName = 'w-full h-full bg-black',
    objectFit = 'contain',
    objectPosition = '50% 50%',
    loop = false,
    playsInline = true,
    preload = 'metadata',
    muted: mutedProp,
    onMutedChange,
    clipStartSeconds = null,
    clipEndSeconds = null,
    onPlay,
    onPause,
    onTimeUpdate,
    onEnded,
    onLoadedMetadata,
    showCenterPlay = true,
    centerPlaySize = 'lg',
    tapAction = 'togglePlay',
    showPlayPauseControl = true,
    chapters = [],
  },
  ref
) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const progressRef = useRef(null);

  const [paused, setPaused] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(mutedProp ?? false);

  const hasClip = clipStartSeconds != null && clipEndSeconds != null;

  useEffect(() => {
    if (mutedProp !== undefined) setMuted(mutedProp);
  }, [mutedProp]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) video.muted = muted;
  }, [muted]);

  const effectiveDuration = hasClip
    ? Math.max(clipEndSeconds - clipStartSeconds, 0)
    : duration;

  const effectiveCurrent = hasClip
    ? Math.max(0, currentTime - clipStartSeconds)
    : currentTime;

  const progressPercent = effectiveDuration > 0
    ? Math.min(100, (effectiveCurrent / effectiveDuration) * 100)
    : 0;

  const syncFromVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setPaused(video.paused);
    setCurrentTime(video.currentTime);
    if (Number.isFinite(video.duration)) setDuration(video.duration);
    setMuted(video.muted);
  }, []);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, []);

  const toggleMute = useCallback(() => {
    const next = !muted;
    setMuted(next);
    onMutedChange?.(next);
  }, [muted, onMutedChange]);

  const toggleFullscreen = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      await el.requestFullscreen?.();
    } else {
      await document.exitFullscreen?.();
    }
  }, []);

  const seekToRatio = useCallback((ratio) => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration)) return;
    const clamped = Math.min(1, Math.max(0, ratio));
    if (hasClip) {
      video.currentTime = clipStartSeconds + clamped * (clipEndSeconds - clipStartSeconds);
    } else {
      video.currentTime = clamped * video.duration;
    }
  }, [clipEndSeconds, clipStartSeconds, hasClip]);

  const seekToSeconds = useCallback((seconds) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = seconds;
    video.play().catch(() => {});
  }, []);

  const handleProgressClick = (e) => {
    const bar = progressRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    seekToRatio((e.clientX - rect.left) / rect.width);
  };

  const handleLoadedMetadata = (e) => {
    const video = e.currentTarget;
    if (hasClip) video.currentTime = clipStartSeconds;
    setDuration(video.duration);
    syncFromVideo();
    onLoadedMetadata?.(e);
  };

  const handleTimeUpdate = (e) => {
    const video = e.currentTarget;
    if (loop && hasClip && video.currentTime >= clipEndSeconds) {
      video.currentTime = clipStartSeconds;
    }
    syncFromVideo();
    onTimeUpdate?.(e);
  };

  const handlePlay = (e) => {
    setPaused(false);
    onPlay?.(e);
  };

  const handlePause = (e) => {
    setPaused(true);
    onPause?.(e);
  };

  const handleEnded = (e) => {
    const video = e.currentTarget;
    if (loop && hasClip) {
      video.currentTime = clipStartSeconds;
      video.play().catch(() => {});
    } else if (!loop) {
      setPaused(true);
    }
    onEnded?.(e);
  };

  const handleVideoTap = () => {
    if (tapAction === 'toggleMute') {
      toggleMute();
    } else {
      togglePlay();
    }
  };

  const centerSizeClass = centerPlaySize === 'sm' ? 'w-16 h-16' : 'w-20 h-20';
  const centerIconClass = centerPlaySize === 'sm' ? 'w-8 h-8' : 'w-10 h-10';

  return (
    <div ref={containerRef} className={`relative overflow-hidden group ${className}`}>
      <video
        ref={mergeRefs(videoRef, ref)}
        src={src}
        poster={poster}
        controls={false}
        playsInline={playsInline}
        preload={preload}
        loop={loop && !hasClip}
        muted={muted}
        className={`${videoClassName} ${objectFit === 'cover' ? 'object-cover' : 'object-contain'}`}
        style={{ objectPosition }}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
        onClick={handleVideoTap}
      />

      {showCenterPlay && paused && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
            className={`${centerSizeClass} gfi-gradient-bg rounded-full flex items-center justify-center text-brand-navy pointer-events-auto transition-all transform hover:scale-110 shadow-2xl`}
            aria-label="Reproducir"
          >
            <SafeIcon name="Play" className={`${centerIconClass} fill-current ml-1`} />
          </button>
        </div>
      )}

      <div
        className="absolute bottom-0 left-0 right-0 z-30 bg-gradient-to-t from-black/80 to-transparent p-4 md:p-6 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full mb-4 pointer-events-auto">
          <div
            ref={progressRef}
            role="slider"
            tabIndex={0}
            aria-label="Progreso del video"
            aria-valuemin={0}
            aria-valuemax={effectiveDuration}
            aria-valuenow={effectiveCurrent}
            onClick={handleProgressClick}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') seekToRatio((effectiveCurrent + 5) / (effectiveDuration || 1));
              if (e.key === 'ArrowLeft') seekToRatio(Math.max(0, effectiveCurrent - 5) / (effectiveDuration || 1));
            }}
            className="relative w-full h-1.5 bg-white/20 rounded-full cursor-pointer"
          >
            <div
              className="h-full gfi-gradient-bg rounded-full relative transition-[width] duration-150 pointer-events-none"
              style={{ width: `${progressPercent}%` }}
            />
            {duration > 0 && chapters.length > 0 && chapters.map((chapter) => {
              const left = Math.min(100, Math.max(0, (chapter.startSeconds / duration) * 100));
              return (
                <button
                  key={`${chapter.startSeconds}-${chapter.title}`}
                  type="button"
                  title={chapter.title}
                  aria-label={`${chapter.title} — ${formatTime(chapter.startSeconds)}`}
                  className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-1 h-3.5 bg-white/90 rounded-sm shadow-sm hover:h-4 hover:bg-brand-cyan transition-all cursor-pointer z-10 p-0 border-0"
                  style={{ left: `${left}%` }}
                  onClick={(e) => {
                    e.stopPropagation();
                    seekToSeconds(chapter.startSeconds);
                  }}
                />
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between text-white text-xs font-black uppercase tracking-widest pointer-events-auto">
          <div className="flex items-center gap-4 md:gap-6">
            {showPlayPauseControl && (
              <button type="button" onClick={togglePlay} aria-label={paused ? 'Reproducir' : 'Pausar'}>
                <SafeIcon name={paused ? 'Play' : 'Pause'} className="w-5 h-5" />
              </button>
            )}
            <span>
              {formatTime(effectiveCurrent)}
              {' / '}
              {formatTime(effectiveDuration)}
            </span>
          </div>
          <div className="flex items-center gap-4 md:gap-6">
            <button type="button" onClick={toggleMute} aria-label={muted ? 'Activar sonido' : 'Silenciar'}>
              <SafeIcon name={muted ? 'VolumeX' : 'Volume2'} className="w-5 h-5" />
            </button>
            <button type="button" onClick={toggleFullscreen} aria-label="Pantalla completa">
              <SafeIcon name="Maximize" className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default BrandVideoPlayer;
